import React, { useState, useEffect } from "react";
import DashboardLayout from "../layouts/DashboardLayout";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { fetchDashboardData } from "../services/dashboardService";

const Dashboard = ({ onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [metrics, setMetrics] = useState({
    totalRevenue: 0,
    totalFreePriceGiven: 0,
    trueTotalFreeQty: 0,
    trueTotalSalesQty: 0,
    productWiseFreeQty: [],
    productSalesPackSizes: []
  });

  const [regionData, setRegionData] = useState([]);
  const [territoryData, setTerritoryData] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetchDashboardData();
        const payload = response?.data || response || {};

        setMetrics({
          totalRevenue: payload.metrics?.totalRevenue || 0,
          totalFreePriceGiven: payload.metrics?.totalFreePriceGiven || 0,
          trueTotalFreeQty: payload.metrics?.trueTotalFreeQty || 0,
          trueTotalSalesQty: payload.metrics?.trueTotalSalesQty || 0, 
          productWiseFreeQty: Array.isArray(payload.metrics?.productWiseFreeQty) ? payload.metrics.productWiseFreeQty : [],
          productSalesPackSizes: Array.isArray(payload.metrics?.productSalesPackSizes) ? payload.metrics.productSalesPackSizes : []
        });

        setRegionData(Array.isArray(payload.regionData) ? payload.regionData : []);
        setTerritoryData(Array.isArray(payload.territoryData) ? payload.territoryData : []);

      } catch (err) {
        console.error("Failed to fetch dashboard data", err);
        setError("Could not connect to the server to load live data.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'BDT', maximumFractionDigits: 0 }).format(value || 0);
  };

  const formatYAxis = (value) => {
    if (value >= 10000000) return (value / 10000000).toFixed(1) + 'Cr';
    if (value >= 100000) return (value / 100000).toFixed(1) + 'L';
    if (value >= 1000) return (value / 1000).toFixed(1) + 'K';
    return value;
  };
  return (
    <DashboardLayout onLogout={onLogout}>
      <div className="p-8 max-w-7xl mx-auto w-full h-screen overflow-y-auto bg-gray-50/50">

        {/* PAGE HEADING */}
        <div className="mb-8 flex justify-between items-end">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
              Dashboard Overview
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Monitor key metrics, free distributions, and sales figures in real-time.
            </p>
          </div>
          {loading && (
            <span className="text-sm text-indigo-600 animate-pulse font-medium">Syncing live data...</span>
          )}
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 rounded-lg text-sm font-medium">
            {error}
          </div>
        )}

        {/* METRIC CARDS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">

          {/* Card 1: Total Revenue */}
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow group flex flex-col justify-between">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Total Revenue</p>
                <h3 className="text-2xl font-bold text-slate-800 tracking-tight">
                  {loading ? '...' : formatCurrency(metrics.totalRevenue)}
                </h3>
              </div>
              <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-4">Gross sales value recorded</p>
          </div>

          {/* Card 2: Total Free Price Given */}
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow group flex flex-col justify-between">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Total Free Price Given</p>
                <h3 className="text-2xl font-bold text-slate-800 tracking-tight">
                  {loading ? '...' : formatCurrency(metrics.totalFreePriceGiven)}
                </h3>
              </div>
              <div className="p-3 bg-rose-50 text-rose-600 rounded-xl group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5a2 2 0 10-2 2h2zm0 0h4.5M12 3v1m0 16v1m-9-9h18" /></svg>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-4">Total monetary worth of free stock</p>
          </div>

          {/* Card 3: Product Wise Free Qty Given */}
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow group">
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Product Wise Free Qty</p>
                <h3 className="text-2xl font-bold text-slate-800 tracking-tight">
                  {loading ? '...' : `${metrics.trueTotalFreeQty.toLocaleString()} Units`}
                </h3>
              </div>
              <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
              </div>
            </div>
            <div className="max-h-20 overflow-y-auto pr-1 text-xs text-slate-600 divide-y divide-slate-100">
              {metrics.productWiseFreeQty.length === 0 && !loading ? (
                <p className="text-slate-400 py-1">No free qty data</p>
              ) : (
                metrics.productWiseFreeQty.map((item, idx) => (
                  <div key={idx} className="flex justify-between py-1">
                    <span className="truncate max-w-[110px] font-medium text-slate-700">{item.product}</span>
                    <span className="text-slate-500 font-semibold">{item.freeQty} units</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Card 4: Product Sales with Pack Sizes */}
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow group">
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Sales by Pack Size</p>
                <h3 className="text-2xl font-bold text-slate-800 tracking-tight">
                  {loading ? '...' : `${metrics.trueTotalSalesQty.toLocaleString()} Units`}
                </h3>
              </div>
              <div className="p-3 bg-amber-50 text-amber-600 rounded-xl group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg>
              </div>
            </div>
            <div className="max-h-20 overflow-y-auto pr-1 text-xs text-slate-600 divide-y divide-slate-100">
              {metrics.productSalesPackSizes.length === 0 && !loading ? (
                <p className="text-slate-400 py-1">No sales data</p>
              ) : (
                metrics.productSalesPackSizes.map((item, idx) => (
                  <div key={idx} className="flex justify-between py-1">
                    <span className="truncate max-w-[100px] font-medium text-slate-700">{item.product} ({item.packSize})</span>
                    <span className="text-slate-500 font-semibold">{item.totalSalesQty} units</span>
                  </div>
                ))
              )}
            </div>
          </div>

        </div>

        {/* CHARTS SECTION */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">

        

          {/* Chart 1: Territory Wise Data */}
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <h2 className="text-lg font-bold text-slate-800 mb-4">Territory Wise Sales Volume</h2>
            
            <div className="flex w-full" style={{ height: 320 }}>
              
              {/* FIXED Y-AXIS (Left Side) - Widened to 100px with left margin */}
              <div style={{ width: '100px', flexShrink: 0, backgroundColor: '#fff', zIndex: 10 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={territoryData} margin={{ top: 5, right: 0, bottom: 40, left: 10 }}>
                    <XAxis dataKey="territory" hide={true} />
                    <YAxis 
                      tickFormatter={formatYAxis} 
                      width={90} 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fill: '#64748b', fontSize: 12 }} 
                    />
                    <Bar dataKey="sales" fill="transparent" isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* SCROLLABLE BARS (Right Side) */}
              <div style={{ flex: 1, overflowX: 'auto', overflowY: 'hidden' }} className="pb-2">
                <div style={{ minWidth: `${territoryData.length * 50}px`, height: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={territoryData} margin={{ top: 5, right: 20, bottom: 40, left: 10 }} barSize={35}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis 
                        dataKey="territory" 
                        axisLine={false} 
                        tickLine={false} 
                        angle={-45} 
                        textAnchor="end" 
                        tick={{ fill: '#64748b', fontSize: 12 }} 
                      />
                      <YAxis hide={true} />
                      <Tooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                      <Bar dataKey="sales" fill="#4f46e5" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

            </div>
          </div>
          {/* Chart 2: Region Wise Data */}
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <h2 className="text-lg font-bold text-slate-800 mb-4">Region Wise Sales Volume</h2>
            
            <div className="flex w-full" style={{ height: 320 }}>
              
              {/* FIXED Y-AXIS (Left Side) - Widened to 100px with left margin */}
              <div style={{ width: '100px', flexShrink: 0, backgroundColor: '#fff', zIndex: 10 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={regionData} margin={{ top: 5, right: 0, bottom: 40, left: 10 }}>
                    <XAxis dataKey="region" hide={true} />
                    <YAxis 
                      tickFormatter={formatYAxis} 
                      width={90} 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fill: '#64748b', fontSize: 12 }} 
                    />
                    <Bar dataKey="sales" fill="transparent" isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* SCROLLABLE BARS (Right Side) */}
              <div style={{ flex: 1, overflowX: 'auto', overflowY: 'hidden' }} className="pb-2">
                <div style={{ minWidth: `${regionData.length * 50}px`, height: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={regionData} margin={{ top: 5, right: 20, bottom: 40, left: 10 }} barSize={35}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis 
                        dataKey="region" 
                        axisLine={false} 
                        tickLine={false} 
                        angle={-45} 
                        textAnchor="end" 
                        tick={{ fill: '#64748b', fontSize: 12 }} 
                      />
                      <YAxis hide={true} />
                      <Tooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                      <Bar dataKey="sales" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              
            </div>
          </div>

        </div>

      </div>
    </DashboardLayout>
  );
};

export default Dashboard;